import { ReactNode, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  AppBar, Box, Drawer, IconButton, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Toolbar, Typography, Avatar, Menu,
  MenuItem, Divider,
} from '@mui/material'
import {
  Menu as MenuIcon, Dashboard, Scanner, Settings, Logout,
  Assessment, Description, MenuBook, Business, Sensors,
  AdminPanelSettings, TrendingUp, VerifiedUser, Payment, People, Rule, Inventory2,
  Gavel, Hub, AccountTree, AttachMoney, TravelExplore,
} from '@mui/icons-material'
import { logout } from '../store/authSlice'
import { AppDispatch, RootState } from '../store'
import { useEntitlements } from '../hooks/useEntitlements'

const drawerWidth = 240

interface LayoutProps {
  children: ReactNode
}

const Layout = ({ children }: LayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((state: RootState) => state.auth)

  const isAdmin = user?.type === 'admin'
  const { has } = useEntitlements()
  const hasTopology = has('topology')

  // Platform admin sees everything; tenant user sees only their scoped items
  const mainItems = isAdmin
    ? [
        { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
        { text: 'Tenants', icon: <Business />, path: '/tenants' },
        { text: 'Scanners', icon: <Sensors />, path: '/scanners' },
      ]
    : [
        { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
        { text: 'Scanners', icon: <Sensors />, path: '/scanners' },
      ]

  const operationsItems = [
    { text: 'Agents', icon: <Inventory2 />, path: '/agents' },
    // Topology is an Enterprise entitlement. Platform admins always see it,
    // matching the backend gate's ops bypass.
    ...(isAdmin || hasTopology
      ? [{ text: 'Topology', icon: <AccountTree />, path: '/topology' }]
      : []),
    // Agent Reach has no nav entry of its own, deliberately. The knowledge
    // layer is a lens on questions people already ask — what does this agent
    // touch (agent card), who depends on this store (Blast Radius), do two
    // repositories disagree (Findings), what does it cost (Cost), what do I
    // have (Registry) — and a destination for it was a page nobody had a
    // reason to open.
    { text: 'Analyses', icon: <Assessment />, path: '/analyses' },
    { text: 'Reports', icon: <Description />, path: '/reports' },
    { text: 'Certificates', icon: <VerifiedUser />, path: '/certificates' },
    { text: 'Compliance', icon: <Gavel />, path: '/compliance' },
    { text: 'Cost', icon: <AttachMoney />, path: '/cost' },
    ...(isAdmin
      ? [{ text: 'Knowledge Base', icon: <MenuBook />, path: '/knowledge-base' }]
      // Named for the process, not for one step in it. A rule is extracted
      // from an imported policy and then reviewed; offering "Custom Rules"
      // as the destination advertised the form rather than the pipeline it
      // belongs to. Route unchanged so existing links keep working.
      : [{ text: 'Custom Enterprise Frameworks', icon: <Rule />, path: '/custom-rules' }]),
  ]

  const systemItems = isAdmin
    ? [
        { text: 'Analytics', icon: <TrendingUp />, path: '/analytics' },
        { text: 'Admin', icon: <AdminPanelSettings />, path: '/admin' },
        { text: 'Integrations', icon: <Hub />, path: '/integrations' },
        { text: 'Settings', icon: <Settings />, path: '/settings' },
      ]
    : [
        { text: 'Team', icon: <People />, path: '/team' },
        { text: 'Billing', icon: <Payment />, path: '/billing' },
        { text: 'Integrations', icon: <Hub />, path: '/integrations' },
        { text: 'Settings', icon: <Settings />, path: '/settings' },
      ]

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen)
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
  const handleMenuClose = () => setAnchorEl(null)
  const handleLogout = () => { dispatch(logout()); handleMenuClose(); navigate('/') }

  const isSelected = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/')

  const renderSection = (title: string, items: typeof mainItems) => (
    <>
      <Typography variant="overline" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}>
        {title}
      </Typography>
      <List dense disablePadding>
        {items.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={isSelected(item.path)}
              onClick={() => { navigate(item.path); setMobileOpen(false) }}
              sx={{ py: 0.75 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  )

  const drawer = (
    <div>
      <Toolbar sx={{ borderBottom: 1, borderColor: 'divider', flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}>
        <Box component="img" src="/brand/logo-horizontal.svg" alt="ARIAS" sx={{ height: 28, width: 'auto', display: 'block' }} />
        <Typography variant="caption" noWrap sx={{ color: 'text.secondary', fontSize: '0.6rem', lineHeight: 1.2, mt: 0.5 }}>
          The Control Plane for AI Agents
        </Typography>
      </Toolbar>
      {renderSection(isAdmin ? 'Platform' : 'Overview', mainItems)}
      <Divider sx={{ my: 0.5 }} />
      {renderSection('Data', operationsItems)}
      <Divider sx={{ my: 0.5 }} />
      {renderSection('System', systemItems)}
    </div>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` } }}
      >
        <Toolbar>
          <IconButton
            color="inherit" aria-label="open drawer" edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Ship Agents Confidently. Every Commit.
          </Typography>
          <IconButton onClick={handleMenuClick} sx={{ p: 0 }}>
            <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32, fontSize: '0.9rem' }}>
              {user?.full_name?.[0] || 'U'}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem disabled>
              <Typography variant="body2">{user?.full_name || 'Admin'}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent" open
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  )
}

export default Layout
